import { useEffect, useMemo } from 'react';

import pluralize from 'pluralize';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import Spinner from 'src/app/components/Elements/Spinner';
import { SimpleFeatureLayout } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import { EscriptOnboardingStepName } from 'src/app/components/Pages/EscriptOnboarding/types';
import { useActivePendingMedicationList } from 'src/app/hooks/data/medication';
import { usePatientList } from 'src/app/hooks/data/patient';
import { useMedicationDetails } from 'src/app/hooks/data/prescription';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { newOnboardingStep, medications } from 'src/app/router/paths';
import { getPrimaryPatientFromList } from 'src/app/utils/data/patient';

const UnorderedList = styled.ul`
  list-style: none;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.s5};

  & > li {
    display: flex;
    align-items: center;
  }

  & > li > svg {
    margin-right: ${({ theme }) => theme.space.s2};
    width: 24px;
    height: 24px;
  }
`;

const PillBottle = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.5 7H5.00174C5.01385 4.82497 5.1103 3.64706 5.87868 2.87868C6.75736 2 8.17157 2 11 2H13C15.8284 2 17.2426 2 18.1213 2.87868C18.8897 3.64706 18.9862 4.82497 18.9983 7H19.5C20.0523 7 20.5 7.44772 20.5 8C20.5 8.55228 20.0523 9 19.5 9H4.5C3.94772 9 3.5 8.55228 3.5 8C3.5 7.44772 3.94772 7 4.5 7ZM7 6C7 5.05719 7 4.58579 7.29289 4.29289C7.58579 4 8.05719 4 9 4H15C15.9428 4 16.4142 4 16.7071 4.29289C17 4.58579 17 5.05719 17 6V7H7V6Z"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5 10L5 16C5 18.8284 5 20.2426 5.87868 21.1213C6.75736 22 8.17157 22 11 22H13C15.8284 22 17.2426 22 18.1213 21.1213C19 20.2426 19 18.8284 19 16V10L5 10ZM14.2929 12.2929C14 12.5858 14 13.0572 14 14V18.5C14 18.9659 14 19.1989 14.0761 19.3827C14.1776 19.6277 14.3723 19.8224 14.6173 19.9239C14.8011 20 15.0341 20 15.5 20C15.9659 20 16.1989 20 16.3827 19.9239C16.6277 19.8224 16.8224 19.6277 16.9239 19.3827C17 19.1989 17 18.9659 17 18.5V12H16C15.0572 12 14.5858 12 14.2929 12.2929Z"
    />
  </svg>
);

export const EscriptOnboardingWelcomeStep = () => {
  useSegmentEvent('viewWelcomeScreen', { shouldTrackOnMount: true, payload: { page_number: 1 } });

  const { replace, push } = useHistory();

  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';
  const { data: patients } = usePatientList(parseInt(accountId), Boolean(accountId));
  const primaryPatient = patients ? getPrimaryPatientFromList(patients) : null;
  const firstName = primaryPatient?.firstName || '';

  const { data: activePendingMedications, isLoading: isActivePendingMedsLoading } = useActivePendingMedicationList(
    user ? parseInt(user.accountId) : -1,
    Boolean(user)
  );

  const activeMedications = activePendingMedications?.active;
  const firstActiveMedItems = useMemo(
    () => (activeMedications?.length ? activeMedications[0]?.items ?? [] : []),
    [activeMedications]
  );

  const { data: medicationDetails } = useMedicationDetails(
    firstActiveMedItems.length > 0 ? parseInt(firstActiveMedItems[0].id) : -1,
    firstActiveMedItems.length > 0
  );

  const provider = medicationDetails?.provider;

  const providerName =
    provider?.firstName && provider?.lastName ? `${provider.firstName} ${provider.lastName}` : 'Your doctor';

  useEffect(() => {
    if (activePendingMedications && firstActiveMedItems.length === 0) {
      replace(medications.route);
    }
  }, [activePendingMedications, firstActiveMedItems, replace]);

  if (isActivePendingMedsLoading) {
    return <Spinner isFullViewportSpinner />;
  }

  return (
    <SimpleFeatureLayout
      backButton={{ isRendered: false }}
      keyName={EscriptOnboardingStepName.welcome}
      title={
        <>
          Hi {firstName},
          <br /> Welcome to Capsule
        </>
      }
      body={{
        content: (
          <>
            <p>
              <span>{providerName}</span> sent us {firstActiveMedItems.length}{' '}
              {pluralize('prescription', firstActiveMedItems.length)} that{' '}
              {firstActiveMedItems.length > 1 ? 'are' : 'is'} ready for delivery.
            </p>

            <UnorderedList>
              {firstActiveMedItems.map(({ name, dosage }) => {
                const medicationText = name.concat(dosage ? ` ${dosage}` : '');

                return (
                  <li key={medicationText}>
                    <PillBottle /> <span>{medicationText}</span>
                  </li>
                );
              })}
            </UnorderedList>

            <p>Let’s get you started with Capsule. It’ll only take a minute.</p>
          </>
        ),
      }}
      cta1={{
        children: 'Continue',
        onClick: () => push(newOnboardingStep.toPath({ stepName: EscriptOnboardingStepName.how_it_works })),
      }}
    />
  );
};
